import type {Evaluator, Version} from "@cm/graph-v3"
import {CircularRef, versionChain} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {hashObject} from "@cm/utils"
import {ImageNode} from "#template/nodes/image/image-node"
import type {OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {MeshLikeNode, ObjectLikeNode} from "#template/nodes/types"
import {meshLike} from "#template/nodes/object/mesh/fundamental"
import {objectLike} from "#template/nodes/object/fundamental"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {ImageGenerator} from "@cm/material-nodes"
import {materialConnection, DistanceTexture as MaterialNodeDistanceTexture, Value} from "@cm/material-nodes"
import type {ObjectData} from "#template/nodes/object/types"
import type {MeshObjectData} from "#template/nodes/object/mesh/types"
import {SceneNodes} from "#template/interfaces/scene-object"
import {visitNone} from "#template/template-node/visit-helpers"

export type DistanceTextureParameters = {
    name: string
    source: MeshLikeNode | null
    uvChannel: number
    range: number
    mergeRange: number
    width: number
    height: number
    targets: ObjectLikeNode[]
    innerValue?: number
    smoothPasses: number
}

export type DistanceTextureConstructorParameters = Omit<DistanceTextureParameters, "source" | "uvChannel" | "mergeRange" | "smoothPasses"> & {
    source?: MeshLikeNode | null
    uvChannel?: number
    mergeRange?: number
    smoothPasses?: number
}

type V0 = {
    name: string
    range: number
    width: number
    height: number
    target: ObjectLikeNode | null
    innerValue?: number
}

type V1 = Omit<V0, "target"> & {
    source: MeshLikeNode | null
    uvChannel: number
    targets: ObjectLikeNode[]
    mergeRange: number
    smoothPasses: number
}

const v0: Version<V0, V1> = {
    toNextVersion: (parameters) => {
        const {target, ...rest} = parameters
        if (target instanceof CircularRef) throw new Error("Cannot resolve circular references when going from v0 to v1 in distance texture node")
        return {
            source: null,
            uvChannel: 0,
            mergeRange: 0,
            targets: target ? [target] : [],
            smoothPasses: 0,
            ...rest,
        }
    },
}

const distanceTextureDefinition: TemplateNodeDefinition<DistanceTextureParameters, DistanceTextureConstructorParameters> = {
    nodeClass: "DistanceTexture",
    validation: {
        parameters: z.object({
            name: z.string(),
            source: z._default(z.nullable(meshLike), null),
            uvChannel: z._default(z.number(), 0),
            range: z.number(),
            mergeRange: z._default(z.number(), 0),
            width: z.number(),
            height: z.number(),
            targets: z.array(objectLike),
            innerValue: z.optional(z.number()),
            smoothPasses: z._default(z.number(), 0),
        }),
        returns: getTemplateNodeReturns(),
    },
    versionChain: versionChain([v0]),
}

@registerTemplateGraphNode
export class DistanceTexture extends ImageNode<DistanceTextureParameters, DistanceTextureConstructorParameters> {
    public override get definition() {
        return distanceTextureDefinition
    }

    public override onFilterActive(_context: OnFilterActiveContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<DistanceTextureParameters> {
        const {source, targets} = this.parameters
        if (!source || targets.length === 0) return skipped
        return visitNone(this.parameters)
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<ImageGenerator | null> {
        const {source, targets, ...rest} = this.parameters

        const evaluatedSource = evaluator.evaluate(source)
        const evaluatedTargets = scope.filterInvalid(scope.list(targets.map((target) => evaluator.evaluate(target))))

        return scope.pureLambda<
            [MeshObjectData | null, number, ObjectData[], number, number, number, number, number | undefined, number],
            ImageGenerator | null
        >(
            scope.tuple(
                evaluatedSource,
                rest.uvChannel,
                evaluatedTargets,
                rest.range,
                rest.mergeRange,
                rest.width,
                rest.height,
                rest.innerValue,
                rest.smoothPasses,
            ),
            ([inputSource, uvChannel, inputTarget, range, mergeRange, width, height, innerValue, smoothPasses]) => {
                if (!inputSource) return null

                const targets = [...new Set(inputTarget.map((target) => [...target.preDisplayList, ...target.displayList]).flat())].filter(
                    (target) => SceneNodes.Mesh.is(target) || SceneNodes.Seam.is(target),
                )
                targets.sort((a, b) => a.id.localeCompare(b.id))
                targets.forEach((target) => {
                    if (SceneNodes.Seam.is(target)) target.item.sort((a, b) => a.id.localeCompare(b.id))
                })

                const getTargetHashableObject = (target: SceneNodes.Mesh | SceneNodes.Seam, inputSource: MeshObjectData) => {
                    const relativeTransform = target.transform.multiply(inputSource.matrix.inverse())
                    if (SceneNodes.Mesh.is(target)) return {id: target.completeMeshData.abstract.contentHash, transform: relativeTransform}
                    else
                        return {
                            ids: target.item.map((x) => x.completeMeshData.abstract.contentHash),
                            curvePoints: target.completeCurveData.reified?.segments,
                            transform: relativeTransform,
                        }
                }

                if (targets.length === 0) return null

                return {
                    imageNode: {
                        generator: ({uv, extension, interpolation, projection, forceOriginalResolution}) => {
                            return {
                                color: materialConnection(
                                    new MaterialNodeDistanceTexture({
                                        parameters: {
                                            inputs: {
                                                vector: uv,
                                            },
                                            settings: {
                                                extension,
                                                interpolation,
                                                projection,
                                                meshObjectData: inputSource,
                                                range,
                                                mergeRange,
                                                width,
                                                height,
                                                forceOriginalResolution,
                                                uvChannel,
                                                targets,
                                                innerValue,
                                                smoothPasses,
                                            },
                                        },
                                    }),
                                    "color",
                                ),
                                alpha: materialConnection(
                                    new Value({
                                        parameters: {
                                            inputs: {},
                                            settings: {
                                                value: 1,
                                            },
                                        },
                                    }),
                                    "value",
                                ),
                            }
                        },
                        hash: hashObject({
                            type: "DistanceTexture",
                            input: inputSource.completeMeshData.abstract.contentHash,
                            targets: targets.map((target) => getTargetHashableObject(target, inputSource)),
                            range,
                            width,
                            height,
                            innerValue,
                        }),
                    },
                    metadata: {
                        width,
                        height,
                    },
                }
            },
            "distanceTexture",
        )
    }
}
